/**
 * Search Utility Functions
 * 
 * Utilities for implementing multi-term search functionality and search result highlighting.
 */

import React from 'react';
import { globalSearchConfig } from './global-search-config';

/**
 * Split search query into individual terms and check if all terms match in the record
 * 
 * @param searchQuery - The search string to split and match
 * @param record - The record object to search in
 * @param searchableFields - Array of field names to search in
 * @param caseSensitive - Whether search should be case sensitive
 * @returns true if all search terms are found somewhere in the searchable fields
 */
export function matchesAllTerms(
  searchQuery: string,
  record: Record<string, any>,
  searchableFields: string[],
  caseSensitive: boolean = false
): boolean {
  if (!searchQuery.trim()) {return true;}
  
  // Split search query by spaces and filter out empty strings
  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  
  // Get all searchable field values as strings
  const searchableText = searchableFields
    .map(field => {
      const value = record[field];
      return value != null ? String(value) : '';
    })
    .join(' ');
  
  const textToSearch = caseSensitive ? searchableText : searchableText.toLowerCase();
  
  // Check if ALL terms are found somewhere in the searchable text
  const result = searchTerms.every(term => {
    const termToMatch = caseSensitive ? term : term.toLowerCase();
    return textToSearch.includes(termToMatch);
  });
  
  return result;
}

/**
 * Highlight search terms in text by wrapping them with a span element
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns React element array with highlighted terms
 */
export function highlightSearchTerms(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): React.ReactElement[] | string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  // Use provided options or fall back to global config
  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Split text by matches and create elements
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    // Check if this part matches any search term
    const isMatch = searchTerms.some(term => {
      const termToMatch = caseSensitive ? term : term.toLowerCase();
      const partToMatch = caseSensitive ? part : part.toLowerCase();
      return termToMatch === partToMatch;
    });

    if (isMatch) {
      return React.createElement(
        'span',
        {
          key: index,
          className,
          style,
        },
        part
      );
    }
    return part;
  }) as React.ReactElement[];
}

/**
 * Highlight a phone number by matching DIGITS ONLY.
 *
 * Mirrors the backend phone search (admin user list): both strip every
 * non-digit from the search term and from the phone, then compare digits, so
 * formatting on either side is irrelevant. The matched run is highlighted in
 * the original formatted string (interior punctuation included), keeping the
 * yellow highlight and the server-side filter in lock-step.
 *
 * @param phone - The formatted phone string to highlight
 * @param searchQuery - The search query (any formatting)
 * @param options - Optional highlight styling overrides
 * @returns React element array with highlighted digit runs, or the original string
 */
export function highlightPhoneDigits(
  phone: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
  }
): React.ReactElement[] | string {
  if (!globalSearchConfig.enableHighlighting || !phone || !searchQuery) {
    return phone || '';
  }

  // Reduce each whitespace-separated term to its digits; drop non-digit terms.
  const digitTerms = searchQuery
    .trim()
    .split(/\s+/)
    .map((t) => t.replace(/\D/g, ''))
    .filter((t) => t.length > 0);
  if (digitTerms.length === 0) {
    return phone;
  }

  // Map each digit's position in the digit-only string back to its index in
  // the formatted string.
  const digitToFormatted: number[] = [];
  let digitsOnly = '';
  for (let i = 0; i < phone.length; i++) {
    const ch = phone[i];
    if (ch >= '0' && ch <= '9') {
      digitToFormatted.push(i);
      digitsOnly += ch;
    }
  }
  if (digitsOnly.length === 0) {
    return phone;
  }

  // Collect formatted-string [start, end) ranges to highlight.
  const ranges: Array<[number, number]> = [];
  for (const term of digitTerms) {
    let from = 0;
    let idx = digitsOnly.indexOf(term, from);
    while (idx !== -1) {
      const startFmt = digitToFormatted[idx];
      const endFmt = digitToFormatted[idx + term.length - 1] + 1;
      ranges.push([startFmt, endFmt]);
      from = idx + 1;
      idx = digitsOnly.indexOf(term, from);
    }
  }
  if (ranges.length === 0) {
    return phone;
  }

  // Merge overlapping/adjacent ranges so nested spans don't double-wrap.
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of ranges) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) {
      last[1] = Math.max(last[1], e);
    } else {
      merged.push([s, e]);
    }
  }

  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;

  const parts: React.ReactElement[] = [];
  let cursor = 0;
  let key = 0;
  for (const [s, e] of merged) {
    if (cursor < s) {
      parts.push(React.createElement(React.Fragment, { key: key++ }, phone.slice(cursor, s)));
    }
    parts.push(React.createElement('span', { key: key++, className, style }, phone.slice(s, e)));
    cursor = e;
  }
  if (cursor < phone.length) {
    parts.push(React.createElement(React.Fragment, { key: key++ }, phone.slice(cursor)));
  }
  return parts;
}

/**
 * Highlight search terms in text and return as HTML string
 * Useful for non-React contexts or when dangerouslySetInnerHTML is needed
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns HTML string with highlighted terms
 */
export function highlightSearchTermsHTML(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Convert style object to CSS string
  const styleString = style
    ? Object.entries(style)
        .map(([key, value]) => {
          // Convert camelCase to kebab-case
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${cssKey}: ${value}`;
        })
        .join('; ')
    : '';

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Replace matches with highlighted spans
  return text.replace(regex, (match) => {
    const classAttr = className ? `class="${className}"` : '';
    const styleAttr = styleString ? `style="${styleString}"` : '';
    const attrs = [classAttr, styleAttr].filter(Boolean).join(' ');
    return `<span ${attrs}>${match}</span>`;
  });
}

/**
 * Escape special regex characters in a string
 * 
 * @param string - The string to escape
 * @returns Escaped string safe for use in regex
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}